import { splitLocalePath, withLocale } from "../i18n/localePath";
import { Paths } from "./paths";

/** Query param that carries the page to open after login or signup. */
export const RETURN_TO_PARAM = "returnTo";

const MAX_RETURN_LENGTH = 2048;

/** C0 controls, DEL, and backslash. Kept out of a regex so lint stays quiet. */
function hasControlCharOrBackslash(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return true;
  }
  return false;
}

/**
 * Auth screens must never be a post-login destination. Sending someone back
 * to /login after they just signed in loops, and a referral short link
 * immediately sends them to signup again.
 */
const AUTH_PATHS = new Set<string>([
  Paths.LOGIN,
  Paths.SIGN_UP,
  Paths.VERIFY_EMAIL,
  Paths.FORGOT_PASSWORD,
  Paths.CHECK_YOUREMAIL,
  Paths.SET_NEW_PASSWORD,
  Paths.PASSWORD_RESET,
  Paths.GOOGLE_EXTERNAL_AUTH,
]);

export function isAuthPath(pathname: string): boolean {
  const bare = splitLocalePath(pathname).pathname;
  if (AUTH_PATHS.has(bare)) return true;
  if (/^\/r\/[^/]+$/.test(bare)) return true;
  return false;
}

/**
 * Accept only an in-app path. Rejects absolute URLs, protocol-relative URLs,
 * and auth screens so a crafted `returnTo` cannot leave the site.
 */
export function sanitizeReturnPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_RETURN_LENGTH) return null;
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.startsWith("/\\")) {
    return null;
  }
  if (hasControlCharOrBackslash(trimmed) || trimmed.includes("://")) return null;

  let url: URL;
  try {
    url = new URL(trimmed, "https://akinda.local");
  } catch {
    return null;
  }

  if (url.origin !== "https://akinda.local") return null;
  if (url.username || url.password) return null;

  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }

  if (
    !decodedPath.startsWith("/") ||
    decodedPath.startsWith("//") ||
    decodedPath.includes("\\") ||
    decodedPath.includes("://")
  ) {
    return null;
  }
  if (isAuthPath(url.pathname) || isAuthPath(decodedPath)) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}

export function readReturnTo(search: string): string | null {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  return sanitizeReturnPath(params.get(RETURN_TO_PARAM));
}

export function readReturnToFromState(state: unknown): string | null {
  if (!state || typeof state !== "object" || !("returnTo" in state)) return null;
  return sanitizeReturnPath((state as { returnTo?: unknown }).returnTo);
}

/** Page to open once a session exists. Query wins over navigation state. */
export function resolvePostAuthDestination(
  location: { pathname?: string; search: string; state?: unknown },
  fallback: string = Paths.HOME,
): string {
  const destination =
    readReturnTo(location.search) ??
    readReturnToFromState(location.state) ??
    fallback;
  const locale = location.pathname ? splitLocalePath(location.pathname).locale : null;
  return locale ? withLocale(destination, locale) : destination;
}

interface ReturnLocation {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
}

/** Where this screen should send the user after authentication. */
export function returnPathFromLocation(location: ReturnLocation): string | null {
  if (isAuthPath(location.pathname)) {
    return readReturnTo(location.search) ?? readReturnToFromState(location.state);
  }
  return sanitizeReturnPath(
    `${location.pathname}${location.search}${location.hash}`,
  );
}

/**
 * Build the next auth URL, keeping attribution params (`ref`, `partner`,
 * `promo`) and attaching a safe return path.
 */
export function continueAuthPath(nextPath: string, location: ReturnLocation): string {
  const hashIndex = nextPath.indexOf("#");
  const hash = hashIndex >= 0 ? nextPath.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? nextPath.slice(0, hashIndex) : nextPath;
  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const search = queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "";
  const params = new URLSearchParams(search);

  if (isAuthPath(location.pathname)) {
    const current = new URLSearchParams(
      location.search.startsWith("?") ? location.search.slice(1) : location.search,
    );
    current.forEach((value, key) => {
      if (key === RETURN_TO_PARAM || params.has(key)) return;
      params.append(key, value);
    });
  }

  const returnTo = returnPathFromLocation(location);
  if (returnTo) params.set(RETURN_TO_PARAM, returnTo);
  else params.delete(RETURN_TO_PARAM);

  const nextSearch = params.toString();
  const href = `${pathname}${nextSearch ? `?${nextSearch}` : ""}${hash}`;
  const locale =
    splitLocalePath(location.pathname).locale ?? splitLocalePath(pathname).locale;
  return locale ? withLocale(href, locale) : href;
}
